import { PageInfo, State } from '#app/types'
import { combineReducers } from 'redux'
import { createSelector } from 'reselect'
import { createAssign, createTable, mustSelectEntity } from 'robodux'
import * as apiService from '#app/services/api'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { wsConnectionRequest, wsDisconnectionRequest } from '#app/actions'

const defaultBaseUrl = process.env.REACT_APP_API_URL || 'https://server.myio.com.br'
const defaultWsUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL?.replace(/^http/, 'ws') || 'wss://server.myio.com.br'
const defaultAuthUrl = process.env.REACT_APP_AUTH_URL || defaultBaseUrl
const baseUrl = createAssign({ name: 'baseUrl', initialState: defaultBaseUrl })
const wsUrl = createAssign({ name: 'wsUrl', initialState: defaultWsUrl })
const authUrl = createAssign({ name: 'authUrl', initialState: defaultAuthUrl })
const pageInfo = createTable<PageInfo>({ name: 'pageInfo' })

export const {
  set: setApiBaseUrl,
  reset: resetApiBaseUrl
} = baseUrl.actions
export const {
  set: setWsUrl,
  reset: resetWsUrl
} = wsUrl.actions
export const {
  set: setAuthUrl,
  reset: resetAuthUrl
} = authUrl.actions
export const {
  add: addPageInfo,
  set: setPageInfo,
  remove: removePageInfo,
  reset: resetPageInfo,
  patch: patchPageInfo
} = pageInfo.actions

export const reducer = combineReducers({
  baseUrl: baseUrl.reducer,
  wsUrl: wsUrl.reducer,
  authUrl: authUrl.reducer,
  pageInfo: pageInfo.reducer
})

const selectToken = (state: State) => state.user.token
const selectUserId = (state: State) => state.user.currentUser
const selectCentralId = (state: State) => state.central.currentCentral
const selectUserCentral = createSelector(
  (state: State) => state,
  selectCentralId,
  (state, id) => state.centraluser.usercentrals[id]
)
export const selectCentralToken = createSelector(
  selectUserCentral,
  c => c?.token
)
export const selectApi = createSelector(
  (state: State) => state.api.baseUrl,
  (state: State) => state.api.authUrl,
  selectToken,
  selectUserId,
  selectCentralId,
  selectCentralToken,
  (baseURL, authURL, cloudToken, userId, centralId, centralToken) => apiService.create({
    baseURL,
    authURL,
    userId,
    cloudToken,
    centralId,
    centralToken
  })
)
const pageInfoSelectors = pageInfo.getSelectors<State>(state => state.api.pageInfo)
const defaultPageInfo: PageInfo = {
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: '',
  endCursor: ''
}
const createPageInfoSelector = mustSelectEntity(defaultPageInfo)
export const selectPageInfoById = createPageInfoSelector(pageInfoSelectors.selectById)

export const useApiData = () => {
  const dispatch = useDispatch()
  const centralId = useSelector(selectCentralId)
  const centralToken = useSelector(selectCentralToken)
  const wsBaseUrl = useSelector((state: State) => state.api.wsUrl)
  useEffect(() => {
    if (centralToken != null) {
      dispatch({ type: 'APP_LOAD_DATA' })
      dispatch(wsConnectionRequest({
        url: `${wsBaseUrl}/websocket?central_id=${centralId}&central_token=${centralToken}`,
        centralId,
        centralToken
      }))
    }
    return () => {
      dispatch({ type: 'APP_RESET_DATA' })
      dispatch(wsDisconnectionRequest())
    }
  }, [dispatch, centralId, centralToken, wsBaseUrl])
}
